<template>
  <div class="community-buildings-page">
    <div class="header">
      <button @click="goBack" class="back-btn">← 返回列表</button>
      <h2>{{ community.name }}</h2>
    </div>

    <div class="building-section">
      <div class="section-header">
        <h3>小区信息</h3>
      </div>

      <div class="table-container">
        <table class="buildings-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>小区名称</th>
              <th>所在地区</th>
              <th>详细地址</th>
              <th>楼栋数</th>
              <th>状态</th>
              <th>创建时间</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ community.id }}</td>
              <td>{{ community.name }}</td>
              <td>{{ community.provinceName || '' }} {{ community.cityName || '' }} {{ community.districtName || '' }}</td>
              <td>{{ community.address }}</td>
              <td>{{ community.building_count }}</td>
              <td><span :class="'status ' + community.status">{{ community.status === 1 ? '启用' : '禁用' }}</span></td>
              <td>{{ community.created_at?.substring(0, 10) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="building-section">
      <div class="section-header">
        <h3>楼栋管理</h3>
        <button @click="showAddModal = true" class="add-btn">+ 添加楼栋</button>
      </div>

      <div class="table-container">
        <table class="buildings-table">
          <thead>
            <tr>
              <th>楼栋编号</th>
              <th>单元数</th>
              <th>是否有电梯</th>
              <th>总楼层</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="building in buildings" :key="building.id">
              <td>{{ building.building_no }}</td>
              <td>{{ building.unit_count }} 个单元</td>
              <td>{{ building.has_elevator === 1 ? '有' : '无' }}</td>
              <td>{{ building.total_floors }} 层</td>
              <td><span :class="'status ' + building.status">{{ building.status === 1 ? '启用' : '禁用' }}</span></td>
              <td>{{ building.created_at?.substring(0, 10) }}</td>
              <td>
                <button @click="editBuilding(building)" class="action-btn edit">编辑</button>
                <button @click="deleteBuilding(building)" class="action-btn delete">删除</button>
              </td>
            </tr>
            <tr v-if="buildings.length === 0">
              <td colspan="7" class="empty">暂无楼栋信息</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingBuilding ? '编辑楼栋' : '添加楼栋' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>楼栋编号</label>
            <input v-model="form.building_no" type="text" class="form-control" placeholder="请输入楼栋编号" />
          </div>
          <div class="form-group">
            <label>单元数</label>
            <input v-model.number="form.unit_count" type="number" class="form-control" placeholder="请输入单元数" min="1" />
          </div>
          <div class="form-group">
            <label>是否有电梯</label>
            <select v-model.number="form.has_elevator" class="form-control">
              <option :value="1">有</option>
              <option :value="0">无</option>
            </select>
          </div>
          <div class="form-group">
            <label>总楼层</label>
            <input v-model.number="form.total_floors" type="number" class="form-control" placeholder="请输入总楼层" min="1" />
          </div>
          <div class="form-group">
            <label>状态</label>
            <select v-model.number="form.status" class="form-control">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveBuilding" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCommunityDetail, getBuildingList, createBuilding, updateBuilding, deleteBuilding } from '@/api/communities'
import { getProvinces, getCities, getDistricts } from '@/api/regions'

export default {
  name: 'CommunityBuildings',
  data() {
    return {
      communityId: null,
      community: {},
      buildings: [],
      showAddModal: false,
      editingBuilding: null,
      form: {
        building_no: '',
        unit_count: 1,
        has_elevator: 1,
        total_floors: 1,
        status: 1
      },
      provinceList: [],
      cityList: [],
      districtList: []
    }
  },
  mounted() {
    this.communityId = this.$route.params.id
    this.loadCommunityDetail()
    this.loadBuildings()
  },
  methods: {
    goBack() {
      this.$router.push('/communities')
    },
    async loadCommunityDetail() {
      try {
        const response = await getCommunityDetail(this.communityId)
        if (response.code === 0) {
          this.community = {
            ...response.data,
            provinceName: '',
            cityName: '',
            districtName: ''
          }
          await this.fillRegionNames(this.community)
        }
      } catch (error) {
      }
    },
    async fillRegionNames(community) {
      try {
        const provinceId = community.provinceId || community.province_id || community.provinceCode
        const cityId = community.cityId || community.city_id || community.cityCode
        const districtId = community.districtId || community.district_id || community.districtCode

        const provinceResponse = await getProvinces()
        if (provinceResponse.code === 0 || provinceResponse.code === 200) {
          this.provinceList = provinceResponse.data || []
          const province = this.provinceList.find(p => p.id === provinceId)
          community.provinceName = province ? province.name : ''
        }

        if (provinceId && cityId) {
          const cityResponse = await getCities(provinceId)
          if (cityResponse.code === 0 || cityResponse.code === 200) {
            const city = cityResponse.data.find(c => c.id === cityId)
            community.cityName = city ? city.name : ''
          }
        }

        if (cityId && districtId) {
          const districtResponse = await getDistricts(cityId)
          if (districtResponse.code === 0 || districtResponse.code === 200) {
            const district = districtResponse.data.find(d => d.id === districtId)
            community.districtName = district ? district.name : ''
          }
        }
      } catch (error) {
      }
    },
    async loadBuildings() {
      try {
        const response = await getBuildingList(this.communityId)
        if (response.code === 0) {
          this.buildings = response.data || []
        }
      } catch (error) {
      }
    },
    editBuilding(building) {
      this.editingBuilding = building
      this.form = {
        building_no: building.building_no,
        unit_count: building.unit_count,
        has_elevator: building.has_elevator,
        total_floors: building.total_floors,
        status: building.status
      }
      this.showAddModal = true
    },
    async deleteBuilding(building) {
      if (confirm(`确定要删除楼栋 ${building.building_no} 吗？`)) {
        try {
          const response = await deleteBuilding(building.id)
          if (response.code === 0) {
            alert('删除成功')
            this.loadBuildings()
          } else {
            alert(response.message || '删除失败')
          }
        } catch (error) {
          alert('删除失败，请稍后重试')
        }
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingBuilding = null
      this.form = {
        building_no: '',
        unit_count: 1,
        has_elevator: 1,
        total_floors: 1,
        status: 1
      }
    },
    async saveBuilding() {
      if (!this.form.building_no.trim()) {
        alert('请输入楼栋编号')
        return
      }
      if (!this.form.unit_count || this.form.unit_count < 1) {
        alert('请输入有效的单元数')
        return
      }
      if (!this.form.total_floors || this.form.total_floors < 1) {
        alert('请输入有效的楼层数')
        return
      }

      try {
        let response
        if (this.editingBuilding) {
          response = await updateBuilding(this.editingBuilding.id, this.form)
        } else {
          response = await createBuilding(this.communityId, this.form)
        }
        if (response && response.code === 0) {
          alert(this.editingBuilding ? '更新成功' : '添加成功')
          this.loadBuildings()
          this.closeModal()
        } else {
          alert(response?.message || '操作失败')
        }
      } catch (error) {
        alert('保存失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.community-buildings-page {
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.back-btn {
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.back-btn:hover {
  background: #f5f5f5;
}

.info-section {
  margin-bottom: 30px;
}

.info-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 20px;
}

.info-card h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #fafafa;
  border-radius: 6px;
  margin-right: 16px;
  margin-bottom: 12px;
  min-width: 220px;
}

.label {
  width: 80px;
  color: #999;
  font-size: 14px;
  white-space: nowrap;
}

.value {
  flex: 1;
  font-size: 14px;
  color: #333;
  margin-left: 8px;
}

.info-content {
  display: flex;
  flex-wrap: wrap;
}

.status {
  border-radius: 20px;
  font-size: 12px;
}

.status.1 { background: #e8f5e9; color: #2ecc71; }
.status.0 { background: #ffebee; color: #e74c3c; }

.building-section {
  margin-top: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
}

.add-btn {
  padding: 8px 16px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.table-container {
  overflow-x: auto;
}

.buildings-table {
  width: 100%;
  border-collapse: collapse;
}

.buildings-table th, .buildings-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.buildings-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.empty {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-btn {
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.delete {
  background: #e74c3c;
  color: #fff;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 450px;
  max-width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
